import React from 'react';
import useSWR from 'swr';
import { fetcher } from 'shared/helpers/axios';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { RingAvatar, NetworkCounts, HEADLINE_SX } from './IdentityBits';

/**
 * The researcher's card on the activity feed: ringed avatar, name, headline, network counts.
 */
/** HIDECOUNTS: no connections / following / consortia (a public post's page, for visitors without an account). */
export default function FeedIdentityCard({ user, name, headline, onOpenProfile, hideCounts = false }) {
  const theme = useTheme();
  const subtitle = headline || user?.position;
  // Same source as the profile page, so both cards always show the same numbers
  const { data: counts } = useSWR(user?.id && !hideCounts ? `/interact/follows/counts/?user_id=${user.id}` : null, fetcher,
    { revalidateOnFocus: false });

  return (
    <Box
      data-testid="feed-identity-card"
      sx={{
        position: 'relative',
        bgcolor: 'background.paper',
        borderRadius: '16px',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        overflow: 'hidden',
        mb: 2,
        px: 2.5,
        pt: 3,
        pb: 2,
        textAlign: 'center',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -60,
          left: '50%',
          width: 260,
          height: 200,
          transform: 'translateX(-50%)',
          // A soft halo in the ring's gradient colours
          background: `radial-gradient(closest-side at 40% 55%, ${alpha('#667eea', 0.13)}, transparent),
                       radial-gradient(closest-side at 60% 55%, ${alpha('#764ba2', 0.1)}, transparent)`,
          pointerEvents: 'none',
        },
      }}
    >
      <RingAvatar src={user?.avatar} name={name} size={84} onClick={onOpenProfile} />

      <Typography sx={{ mt: 1.5, fontSize: '1.45rem', fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
        {name}
      </Typography>
      {subtitle && (
        <Typography sx={{ ...HEADLINE_SX, mt: 0.5, fontSize: '1.2rem', color: 'text.secondary', lineHeight: 1.45 }}>
          {subtitle}
        </Typography>
      )}

      {!hideCounts && (
        <NetworkCounts
          connections={counts ? counts.connections : '–'}
          following={counts ? counts.following_entities : '–'}
          consortia={counts ? counts.consortia : '–'}
          links={{ connections: '/connections/', following: '/social/followed/', consortia: '/consortium/?filter=my' }}
          sx={{ mt: 2 }}
        />
      )}
    </Box>
  );
}
